import type SidebarService from 'teacher-ui-client/services/sidebar'
import { LinkTo } from '@ember/routing'
import { guidFor } from '@ember/object/internals'
import onLinkActive from 'teacher-ui-client/modifiers/on-link-active'
import { array, hash } from '@ember/helper'
import { service } from '@ember/service'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import Component from '@glimmer/component'
import type SubscriptionTypeService from 'teacher-ui-client/services/subscription-type'
import type SideNavService from 'teacher-ui-client/services/side-nav'

interface SideNavItemSignature {
  Args: {
    linkTo?: string
    href?: string
    overrideDisabled?: boolean
    currentWhen?: string
    linkToModels?: unknown[]
    linkToQuery?: Record<string, unknown>
  }
  Blocks: {
    default: []
  }
  Element: HTMLLIElement
}

export class SideNavItem extends Component<SideNavItemSignature> {
  @service subscriptionType!: SubscriptionTypeService

  @service sideNav!: SideNavService

  @service sidebar!: SidebarService

  get isDisabled() {
    return this.sideNav.limitedAccess && !this.args.overrideDisabled
  }

  get activeClassNames() {
    switch (this.subscriptionType.current) {
      case SubscriptionType.Reading:
        return {
          link: 'active-link:text-oceany-blue-350',
          border: 'border-oceany-blue-350',
        }
      case SubscriptionType.Maths:
        return {
          link: 'active-link:text-ms-green-350',
          border: 'border-ms-green-350',
        }
      case SubscriptionType.Writing:
        return {
          link: 'active-link:text-wl-blue-300',
          border: 'border-wl-blue-300',
        }
      default:
        return {
          link: '',
          border: '',
        }
    }
  }

  get elementId() {
    return guidFor(this)
  }

  get isActivated() {
    return this.sidebar.activatedElementId === this.elementId
  }

  <template>
    <li
      id={{this.elementId}}
      class="text-dusty-black-400 px-4 py-2 sm:block {{if this.isActivated this.activeClassNames.border}}"
      role="presentation"
      {{onLinkActive this.sidebar.activate}}
      ...attributes
    >

      {{#if @linkTo}}
        <LinkTo
          role="menuitem"
          data-test-sidenav-accordion-label
          class="text-dusty-black-400 hover:text-dusty-black-400 flex cursor-pointer items-center gap-3 text-sm font-medium select-none
            {{this.activeClassNames.link}}
            {{if this.isDisabled 'pointer-events-none opacity-50'}}"
          aria-disabled={{if this.isDisabled "true" "false"}}
          @route={{@linkTo}}
          @models={{if @linkToModels @linkToModels (array)}}
          @query={{if @linkToQuery @linkToQuery (hash)}}
          @current-when={{@currentWhen}}
        >
          {{yield}}
        </LinkTo>
      {{else if @href}}
        <a
          role="menuitem"
          data-test-sidenav-accordion-label
          class="text-dusty-black-400 hover:text-dusty-black-400 flex cursor-pointer items-center gap-3 text-sm font-medium select-none
            {{this.activeClassNames.link}}
            {{if this.isDisabled 'pointer-events-none opacity-50'}}"
          aria-disabled={{if this.isDisabled "true" "false"}}
          href={{@href}}
        >
          {{yield}}
        </a>
      {{/if}}
    </li>
  </template>
}

export default SideNavItem
